import type { ReactNode } from "react";

import type { AdminSession } from "../types";

/**
 * 開発環境の Drizzle Studio URL
 */
const DRIZZLE_STUDIO_URL = import.meta.env.DEV ? "https://local.drizzle.studio/" : null;

type AppShellProps = {
  activeSection: "support" | "push";
  children: ReactNode;
  session: AdminSession;
  isSigningOut: boolean;
  onNavigate: (path: string) => void;
  onSignOut: () => void;
};

/**
 * 管理機能共通の画面枠
 *
 * @param props.activeSection - 選択中の機能
 * @param props.children - 画面内容
 * @param props.session - 管理者セッション
 * @param props.isSigningOut - サインアウト処理中か
 * @param props.onNavigate - 画面遷移処理
 * @param props.onSignOut - サインアウト処理
 * @returns 管理画面の共通枠
 */
export const AppShell = ({
  activeSection,
  children,
  session,
  isSigningOut,
  onNavigate,
  onSignOut,
}: AppShellProps) => {
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand__mark" aria-hidden="true">
            C
          </span>
          <div className="brand__identity">
            <strong>Codocolle</strong>
            <span>運用管理</span>
          </div>
        </div>

        <nav className="sidebar__nav" aria-label="管理機能">
          <button
            className={`nav-item ${activeSection === "support" ? "nav-item--active" : ""}`}
            type="button"
            aria-current={activeSection === "support" ? "page" : undefined}
            onClick={() => onNavigate("/support")}
          >
            <span className="nav-item__icon" aria-hidden="true">
              ◫
            </span>
            <span>
              <strong>お問い合わせ</strong>
              <small>確認・返信</small>
            </span>
          </button>
          <button
            className={`nav-item ${activeSection === "push" ? "nav-item--active" : ""}`}
            type="button"
            aria-current={activeSection === "push" ? "page" : undefined}
            onClick={() => onNavigate("/push")}
          >
            <span className="nav-item__icon" aria-hidden="true">
              ◇
            </span>
            <span>
              <strong>お知らせ配信</strong>
              <small>作成・配信結果</small>
            </span>
          </button>
          {DRIZZLE_STUDIO_URL === null ? null : (
            <a
              className="nav-item nav-item--link"
              href={DRIZZLE_STUDIO_URL}
              rel="noreferrer"
              target="_blank"
            >
              <span className="nav-item__icon" aria-hidden="true">
                ▦
              </span>
              <span>
                <strong>Drizzle Studio</strong>
                <small>データベースを確認</small>
              </span>
            </a>
          )}
        </nav>

        <div className="sidebar__footer">
          <span className="status-dot" aria-hidden="true" />
          運用サービスに接続中
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <div className="topbar__context">
            <span>Codocolle</span>
            <strong>{activeSection === "support" ? "お問い合わせ対応" : "お知らせ配信"}</strong>
          </div>
          <div className="account-menu">
            <div className="account-menu__avatar" aria-hidden="true">
              {session.admin_user.display_name.slice(0, 1).toUpperCase()}
            </div>
            <div className="account-menu__identity">
              <strong>{session.admin_user.display_name}</strong>
              <span>{session.admin_user.email}</span>
            </div>
            <button
              className="button button--ghost button--compact"
              type="button"
              disabled={isSigningOut}
              onClick={onSignOut}
            >
              {isSigningOut ? "処理中…" : "サインアウト"}
            </button>
          </div>
        </header>

        <main className="main-content">{children}</main>
      </div>
    </div>
  );
};
